import React, { useState, useEffect } from 'react';
import { PlusIcon, CheckIcon } from '@heroicons/react/24/outline';
import { AuthService } from '../../services/auth.service';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { logout, setCredentials } from '../../store/slices/authSlice';
import { config } from '../../config';

interface Profile {
    id: string;
    user_id: string;
    email?: string;
    is_active: boolean;
}

interface ProfileSwitcherProps {
    currentUserId: string;
    onAddProfile: () => void;
    onClose: () => void;
}

const ProfileSwitcher: React.FC<ProfileSwitcherProps> = ({ currentUserId, onAddProfile, onClose }) => {
    const [profiles, setProfiles] = useState<Profile[]>([]);
    const [loading, setLoading] = useState(true);
    const [switching, setSwitching] = useState(false);
    const [switchingId, setSwitchingId] = useState<string | null>(null);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        fetchProfiles();
    }, []);

    const fetchProfiles = async () => {
        try {
            setLoading(true);
            const data = await AuthService.getUserProfiles();
            setProfiles(data.map(profile => ({
                id: profile.user_id,
                user_id: profile.user_id,
                email: profile.email,
                is_active: profile.is_active
            })));
            setError('');
        } catch (err) {
            console.error('Error fetching profiles:', err);
            setError('Failed to load profiles');
        } finally {
            setLoading(false);
        }
    };

    const switchToProfile = async (profileId: string) => {
        try {
            if (profileId === currentUserId) {
                // Already on this profile
                onClose();
                return;
            }

            setSwitching(true);
            setSwitchingId(profileId);
            
            const result = await AuthService.switchProfile(profileId);
            if (result.success) {
                // First, clear the current user's session
                dispatch(logout());
                
                // Clear any cached data for the previous user
                sessionStorage.clear();
                
                // Update localStorage
                localStorage.setItem('token', result.data.token);
                localStorage.setItem('userId', result.data.user.id);
                
                // Get encryption keys from the result
                const encryptedVaultKey = result.data.encryptedVaultKey;
                
                // Update Redux state with new user info
                dispatch(setCredentials({
                    user: {
                        id: result.data.user.id,
                        email: result.data.user.email,
                        two_factor_enabled: false
                    },
                    token: result.data.token,
                    role_id: result.data.role_id,
                    team_id: result.data.team_id,
                    permissions: result.data.permissions
                }));
                
                // CRITICAL: Update the browser extension with new auth data
                if (window.chrome?.runtime?.id) {
                    try {
                        // Show a loading indicator for extension sync
                        const syncToast = toast.loading('Syncing with extension...');
                        
                        chrome.runtime.sendMessage(config.EXTENSION_ID, {
                            type: 'SAVE_AUTH_DATA',
                            payload: {
                                user: {
                                    id: result.data.user.id,
                                    email: result.data.user.email,
                                    name: result.data.user.name || ''  // Include name if available
                                },
                                token: result.data.token,
                                isAuthenticated: true,
                                role_id: result.data.role_id,
                                permissions: result.data.permissions,
                                team_id: result.data.team_id,
                                encryptedVaultKey: result.data.encryptedVaultKey,
                                // Make sure to include ALL encryption keys
                                masterKey: result.data.masterKey,
                                // Add timestamp to ensure extension recognizes this as a new switch
                                timestamp: new Date().getTime()
                            }
                        }, (response) => {
                            // Always dismiss the loading toast
                            toast.dismiss(syncToast);
                            
                            if (chrome.runtime.lastError) {
                                console.error('Failed to notify extension:', chrome.runtime.lastError);
                                // Show error to user
                                toast.error('Extension sync failed. Please try logging in directly in the extension.', {
                                    duration: 5000
                                });
                            } else if (response?.success) {
                                // Show success message
                                toast.success('Profile synced with extension', { 
                                    id: 'extension-sync-success' 
                                });
                                
                                // Important: Clear stored master password when switching profiles
                                chrome.runtime.sendMessage(config.EXTENSION_ID, {
                                    type: 'CLEAR_MASTER_PASSWORD'
                                }, () => {
                                    console.log('Master password cleared on profile switch');
                                });
                                
                                // Force refresh extension data
                                chrome.runtime.sendMessage(config.EXTENSION_ID, {
                                    type: 'FETCH_ENTRIES'
                                });
                                
                                console.log('Extension notified about profile switch');
                            } else {
                                // Handle unexpected response
                                console.error('Extension returned an error:', response?.error);
                                toast.error('Extension sync returned an error', {
                                    duration: 5000
                                });
                            }
                        });
                    } catch (err) {
                        console.error('Error communicating with extension:', err);
                        toast.error('Failed to sync with extension', {
                            duration: 5000
                        });
                    }
                }
                
                toast.success('Switching to profile...');
                navigate('/all-items', { replace: true });
                
                // Close the profile switcher
                onClose();
                
                // This is key: Navigate to a different route first, then back to vaults
                // This forces React Router to remount the components
                setTimeout(() => {
                    window.location.reload();
                    navigate('/all-items', { replace: true });
                    
                    // Then navigate back to vaults after a small delay
                    setTimeout(() => {
                        navigate('/all-items', { replace: true });
                        
                        // Trigger an app-wide refresh event that components can listen for
                        window.dispatchEvent(new CustomEvent('profile-switched'));
                    }, 50);
                }, 100);
            } else {
                toast.error('Failed to switch profile');
                setSwitching(false);
                setSwitchingId(null);
            }
        } catch (err: any) {
            console.error('Error switching profile:', err);
            toast.error(err?.message || 'Error switching profile');
            setSwitching(false);
            setSwitchingId(null);
        }
    };

    if (loading) {
        return <div className="p-4 text-center">Loading profiles...</div>;
    }

    if (error) {
        return <div className="p-4 text-center text-red-500">{error}</div>;
    }

    return (
        <div>
            {profiles.length > 0 && (
                
                <div className="max-h-60 overflow-y-auto">
                    {profiles.filter(profile => profile.user_id !== currentUserId).map((profile) => (
                        <button
                            key={profile.user_id}
                            onClick={() => switchToProfile(profile.user_id)}
                            className="w-full text-left px-4 py-2 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700"
                            disabled={switching}
                        >
                            <div className="flex-1 truncate">
                                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                    { profile.email}
                                </p>
                            </div>
                            {switchingId === profile.user_id ? (
                                <div className="h-5 w-5">
                                    <div className="animate-spin h-5 w-5 border-t-2 border-b-2 border-blue-500 rounded-full"></div>
                                </div>
                            ) : profile.user_id === currentUserId ? (
                                <CheckIcon className="h-5 w-5 text-green-500" />
                            ) : null}
                        </button>
                    ))}
                </div>
            )}
            
            <div className="border-t border-gray-200 dark:border-gray-700 mt-2">
                <button
                    onClick={onAddProfile}
                    className="w-full text-left px-4 py-3 flex items-center text-blue-600 dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                    disabled={switching}
                >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    <span>Add another account</span>
                </button>
            </div>
            
            {switching && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-xl text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
                        <p className="mt-4 text-lg font-medium">Switching profile...</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Please wait while we load your data</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfileSwitcher; 